import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Nyhetsbrev() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Update page title and meta description
    document.title = 'Nyhetsbrev - Easy Accounting AS';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Abonner på Easy Accounting AS sitt nyhetsbrev og hold deg oppdatert på siste nyheter innen regnskap, skatt og økonomi. Ekspertinnsikt og praktiske tips.');
    }
    
    // Add Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Nyhetsbrev - Easy Accounting AS",
      "description": "Abonner på Easy Accounting AS sitt nyhetsbrev og hold deg oppdatert på siste nyheter innen regnskap, skatt og økonomi.",
      "publisher": {
        "@type": "Organization",
        "name": "Easy Accounting AS",
        "url": import.meta.env.VITE_SITE_URL || "https://example.com"
      },
      "mainEntity": {
        "@type": "NewsArticle",
        "headline": "Siste nyheter innen regnskap og økonomi",
        "description": "Oppdateringer fra regnskapsverdenen og våre eksperter",
        "publisher": {
          "@type": "Organization",
          "name": "Easy Accounting AS"
        }
      }
    });
    document.head.appendChild(schemaScript);
    
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const [formData, setFormData] = useState({
    navn: '',
    email: '',
    bedrift: '',
    interesser: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [selectedArticle, setSelectedArticle] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Send til ny Edge Function for lagring i database
      const edgeFunctionResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/save-newsletter-subscriber`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          subscriptionData: formData
        }),
      });

      const result = await edgeFunctionResponse.json();

      if (edgeFunctionResponse.ok && result.success) {
        setSubmitStatus('success');
        setFormData({
          navn: '',
          email: '',
          bedrift: '',
          interesser: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReadMore = (article: any) => {
    setSelectedArticle(article);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedArticle(null);
  };

  const latestNews = [
    {
      id: 10,
      title: 'Nye særavtaler om dekning av utgifter til reise og kost',
      excerpt: 'Særavtalene om dekning av utgifter til reise og kost videreføres med justerte satser fra 1. januar 2026. Kilometergodtgjørelse økes til kr 5,30 per km og nye kostsatser for både innenlands og utenlands reiser.',
      fullContent: `Særavtalene om dekning av utgifter til reise og kost videreføres med justerte satser og gis virkning fra og med 1. januar 2026 til 31. desember 2027.

**Særavtale om dekning av utgifter til reise og kost innenlands**

Særavtale om dekning av utgifter til reise og kost innenlands videreføres uendret med justerte satser og gis virkning fra og med 1. januar 2026 til 31. desember 2027.

**Følgende satser gjelder fra 1. januar 2026:**

**§ 6 Bruk av egen bil**

Satsen for kilometergodtgjørelse er: **kr 5,30 pr. km**

**§ 9 Utgifter til kost**

Arbeidstaker får dekket utgifter til kost på reise over 15 km og som varer utover 6 timer. Udokumenterte utgifter til kost dekkes etter følgende satser:

- **For reiser over 12 timer med overnatting dekkes kost med kr 1012**
- **For reiser fra og med 6 timer og til og med 12 timer dekkes kost med kr 397**
- **For reiser over 12 timer uten overnatting dekkes kost med kr 736**

**Særavtale om dekning av utgifter til reise og kost utenfor Norge**

Særavtale om dekning av utgifter til reise og kost utenfor Norge videreføres uendret med justerte satser og gis virkning fra og med 1. januar 2026 til 31. desember 2027.

Partene er enige om å øke satsen i avtalens § 12. Øvrige nye satser følger av vedlegg til særavtalen.

**§12 Kompensasjonstillegg**

Det gis et kompensasjonstillegg på **kr 674 per døgn** utover 12 timer.

**Praktiske konsekvenser:**

Disse nye satsene vil påvirke:
- Beregning av reisegodtgjørelser for ansatte
- Budsjetter for reisekostnader
- Refusjonsordninger i bedrifter
- Skattefrie godtgjørelser

**Viktige endringer fra 2025:**

Satsene er justert for å reflektere:
- Økte drivstoffpriser og bilhold
- Prisutviklingen på mat og overnatting
- Generell lønns- og prisutvikling
- Endrede reisemønstre

**Råd til arbeidsgivere:**

- Oppdater reisereglement med nye satser
- Informer ansatte om endringene
- Gjennomgå eksisterende godtgjørelsesordninger
- Oppdater lønns- og regnskapssystemer
- Sørg for korrekt dokumentasjon av reiser

**Spesielt viktig for:**

- **Bedrifter med mye reisevirksomhet:** Nye satser påvirker budsjetter
- **Ansatte som reiser mye:** Høyere godtgjørelser
- **Regnskapsførere:** Må oppdatere systemer og rutiner
- **HR-avdelinger:** Må informere og implementere endringer

**Sammenligning med Skatteetatens satser:**

Det er viktig å merke seg at særavtalens satser er høyere enn Skatteetatens standardsatser:
- Kilometergodtgjørelse: kr 5,30 (særavtale) vs kr 3,50 (Skatteetaten)
- Kost over 12 timer med overnatting: kr 1012 (særavtale) vs kr 693 (Skatteetaten)

Forskjellen mellom særavtalens satser og Skatteetatens satser vil være skattepliktig for arbeidstaker.

**Gyldighetsperiode:**

Disse satsene gjelder fra **1. januar 2026 til 31. desember 2027**.

**Dokumentasjon:**

For fullstendig oversikt over alle satser for utenlandsreiser, se vedlegg til særavtalen på regjeringens nettsider.

Disse endringene er viktige for korrekt beregning av reisegodtgjørelser og sikrer at ansatte får rimelig kompensasjon for utgifter på tjenestereiser.`,
      date: '19. desember 2025',
      category: 'Skatt',
      image: 'https://public.readdy.ai/ai/img_res/3e5b88aff76557e9404aada9e782df94.jpg',
      source: 'Regjeringen'
    },
    {
      id: 9,
      title: 'Forskuddssatser for 2026 - Del 2',
      excerpt: 'Nye satser for fri kost og losji, godtgjørelser for hjemmekontor, verktøygodtgjørelse og representasjonskostnader. Viktige oppdateringer for arbeidsgivere og ansatte.',
      fullContent: `Skattedirektoratet har fastsatt ytterligere satser til bruk for forskudd og skattefastsetting for 2026.

**Fri kost og losji**

Den trekk- og skattepliktige fordelen ved helt eller delvis fri kost og losji fastsettes etter følgende satser:

- Fritt opphold (kost og losji): kr 151 per dag
- Fri kost (alle måltider): kr 107 per dag
- Fri kost (to måltider): kr 83 per dag
- Fri kost (ett måltid): kr 55 per dag
- Fritt losji (eget eller delt rom): kr 44 per dag

Det skal bare regnes med det antall dager som arbeidstakeren faktisk mottar slike ytelser.

**Kost for langtransportsjåfører ved kjøring i Norge og i utlandet**

Godtgjørelse til dekning av merkostnader til kost for langtransportsjåfører for kjøring i Norge og i utlandet utbetalt med inntil kr 400 per døgn anses ikke å gi trekk- og skattepliktig overskudd. Satsen inkluderer småutgifter.

**Kost på besøksreise for skattepliktig som har fri kost på arbeidsstedet**

Godtgjørelse inntil kr 115 hver vei til dekning av merkostnader til kost når besøksreisen mellom sted for arbeidsopphold og hjem ikke medfører overnatting, men varer i 6 timer eller mer, anses ikke å gi trekk- eller skattepliktig overskudd.

**Godtgjørelse for losji ved opphold i egen brakke/campingvogn**

Godtgjørelse inntil kr 80 per døgn til skattepliktige som under arbeidsopphold utenfor hjemmet bor i egen brakke/campingvogn, som ikke anses som driftsmiddel, anses ikke å gi trekk- eller skattepliktig overskudd.

**Avgrensning av representasjonskostnader**

- **Uteservering:** Fradrag for kostnader til uteservering kan ikke overstige kr 592 per person per tilfelle
- **Gavegjenstand:** Fradrag for kostnader til gavegjenstand med firmamerke og/eller firmanavn kan ikke overstige kr 324 per gjenstand
- **Oppmerksomheter:** Fradrag for oppmerksomheter overfor forretningsforbindelser kan ikke overstige kr 324 per tilfelle

**Utgiftsgodtgjørelse for barnepassere**

Utgiftsgodtgjørelse til dekning av merkostnader til barnepasshold, ekstra kostnader til vask, oppvarming mv. til den som passer andres barn i eget hjem, herunder driver familiebarnehage som næringsvirksomhet i eget hjem, anses ikke som næringsinntekt når godtgjørelsen utgjør 50 prosent av vederlaget for barnepass, men høyst kr 1 444 per måned for hvert barn.

**Verktøygodtgjørelse for bygningsarbeidere**

Godtgjørelse til bygningsarbeidere på inntil kr 3 388 per år for håndverktøy anses ikke å gi trekk- eller skattepliktig overskudd når bygningsarbeideren holder verktøy i sitt arbeid. Overstiger godtgjørelsen satsen, vil merutbetalingen utgjøre et trekk- og skattepliktig overskudd med mindre full kostnadsdokumentasjon fremlegges.

**Verktøygodtgjørelse for skogsarbeidere**

Godtgjørelse til skogsarbeidere på inntil kr 39 per time til dekning av kostnader ved hold av motorsag og annen redskap (avskrivning, vedlikehold og driftskostnader) i sitt arbeid anses ikke å gi trekk- eller skattepliktig overskudd. Overstiger godtgjørelsen satsen, vil merutbetalingen utgjøre et trekk- og skattepliktig overskudd med mindre det fremlegges full kostnadsdokumentasjon for den delen av eiendommen som brukes i ervervsvirksomhet.

**Godtgjørelse til hjemmekontor**

Godtgjørelse på inntil kr 2 240 per år til dekning av merkostnader knyttet til den delen av eiendommen som brukes i eierens ervervsvirksomhet anses ikke å gi trekk- eller skattepliktig overskudd dersom eieren benytter minst halvparten av eiendommen til egen bolig regnet etter utleieverdien. Overstiger godtgjørelsen satsen, vil merutbetalingen utgjøre et trekk- og skattepliktig overskudd med mindre det fremlegges full kostnadsdokumentasjon for den delen av eiendommen som brukes i ervervsvirksomhet.

**Praktiske konsekvenser:**

Disse nye satsene vil påvirke:
- Beregning av naturalytelser for ansatte
- Skattefrie godtgjørelser til spesielle yrkesgrupper
- Representasjonskostnader for bedrifter
- Hjemmekontor-ordninger

**Viktige endringer fra 2025:**

Satsene er justert for å reflektere:
- Prisutviklingen på mat og losji
- Økte kostnader for verktøy og utstyr
- Endringer i hjemmekontor-praksis
- Generell lønns- og prisutvikling

**Råd til arbeidsgivere:**

- Oppdater lønns- og regnskapssystemer med nye satser
- Gjennomgå eksisterende godtgjørelsesordninger
- Informer ansatte om endringene
- Vurder om naturalytelser må justeres
- Sørg for korrekt dokumentasjon

**Spesielt viktig for:**

- **Byggebransjen:** Nye verktøygodtgjørelser
- **Transportbransjen:** Oppdaterte kostsatser
- **Barnehager:** Nye satser for barnepassere
- **Hjemmekontor:** Justerte godtgjørelser

Disse satsene er viktige for korrekt beregning av skatt og godtgjørelser i 2026.`,
      date: '7. november 2025',
      category: 'Skatt',
      image: 'https://readdy.ai/api/search-image?query=Norwegian%20tax%20rates%20part%202%20for%202026%2C%20free%20meals%20and%20accommodation%20allowances%2C%20home%20office%20compensation%2C%20professional%20accounting%20documents%20with%20calculator%20and%20official%20papers%20showing%20meal%20allowances%20and%20tool%20compensation&width=400&height=250&seq=news-forskuddssatser-2026-del2&orientation=landscape',
      source: 'Sticos'
    },
    {
      id: 8,
      title: 'Forskuddssatser for 2026 - Del 1',
      excerpt: 'Skattedirektoratet har fastsatt satser til bruk for forskudd og skattefastsetting for 2026. Nye satser for kostbesparelse, bilgodtgjørelse, firmabil og mer.',
      fullContent: `Skattedirektoratet har fastsatt satser til bruk for forskudd og skattefastsetting for 2026.

**Kostbesparelse**

Har arbeidstakeren under pendleropphold fri kost eller får kost dekket etter regning, verdsettes den trekk- og skattepliktige kostbesparelsen til kr 107 per døgn.

**Godtgjørelse til kost og losji ved yrkesreiser eller arbeidsopphold med overnatting**

Når godtgjørelse for kost på yrkes-/tjenestereiser med overnatting eller arbeidsopphold utenfor hjemmet utbetales med inntil:

- kr 693 per døgn for skattepliktige som har bodd på hotell mv., og skattepliktig i tillegg selv betaler frokosten eller
- kr 400 per døgn for skattepliktige som har bodd på hybel uten kokemuligheter eller på pensjonat eller brakke

Godtgjørelse for kost på yrkes-/tjenestereise med overnatting kan utbetales med kr 107 per døgn uten at dette gir trekk- og skattepliktig overskudd, når skattepliktige har bodd på hybel med kokemuligheter eller har overnattet privat.

Nattillegg utbetalt i samsvar med bestemmelser i statens reiseregulativ (særavtale eller tariffavtale) for yrkes-/tjenestereiser med inntil kr 452 per døgn og i samsvar med legitimasjonskrav i skattebetalingsforskriften § 5-6-12 tredje ledd, unntatt opplysning om navn og adresse på overnattingssted eller utleier, anses ikke å ha gitt trekk- og skattepliktig overskudd.

**Reiser uten overnatting**

Godtgjørelse til dekning av merkostnader til kost på yrkes-/tjenestereiser på minst 15 km fra reisens utgangspunkt uten overnatting utbetalt med inntil:

- kr 200 for reiser fra og med 6 timer og til og med 12 timer
- kr 400 for reiser over 12 timer

anses ikke å gi trekk- og skattepliktig overskudd.

**Bilgodtgjørelse (kilometergodtgjørelse)**

**Sats per kilometer - uansett kjørelengde:**
- 3,50 kr pr. km

**Tillegg:**
- Passasjertillegg: 1,00 kr pr. km
- Kjøring på skogs- og anleggsvei: 1,00 pr. km
- Tilhenger: 1,00 pr. km

**Andre kjøretøy:**
- Motorbåt til yrkeskjøring: kr 7,50 per km
- Snøscooter og ATV: kr 10,00 per km
- Tung motorsykkel (over 125 ccm): kr 2,95 per km
- Moped eller lett motorsykkel (opp til 125 ccm) og andre motoriserte fremkomstmidler: kr 2,00 per km

**Bilsatser - fordel ved privat bruk av firmabil**

Fordel ved privat bruk av firmabil fastsettes til 30 % av bilens listepris som ny inntil kroner 370 300 og 20 % av overskytende listepris.

**Bilsatser - firmabil hvor fordelen ikke står i forhold til listeprisen**

Er vilkårene oppfylt ved at standardfordelen basert på listepris blir for høy, og firmabilordningen varer hele året, skal den skjønnsmessige fordelen settes til kr 66 000 ved beregningen av forskuddstrekket og ved skattefastsettingen.

**Bilsatser - firmabil som faller utenfor standardreglene**

Ved privat bruk av firmabil som faller utenfor standardreglene beregnes fordelen for arbeidsreiser til og med 4000 km og annen privat kjøring etter en sats på kr 3,40 per km.

For arbeidsreiser som overstiger 4000 km beregnes fordelen etter en sats på kr 1,50 per km.

**Varebiler klasse 2 og lastebiler med totalvekt under 7 501 kg**

Regler for verdsettelse av trekk- og skattepliktig fordel for varebil klasse 2 og lastebil med totalvekt under 7 501 kg følger av forskrift 19. november 1999 nr. 1158 til utfylling og gjennomføring mv. av skatteloven § 5-13-1 fjerde ledd og skattebetalingsforskriften § 5-8-11.

**Beregningsmetoder:**

**Sjablongmetoden (med bunnfradrag):**
- Bunnfradraget i listepris for bil som omfattes er 50 prosent av beregningsgrunnlaget etter skatteloven § 5-13
- Bunnfradraget kan ikke overstige 150 000 kroner
- Innslagspunkt for trinn 2 gjelder tilsvarende

**Individuell verdsettelse:**
Når vilkårene for individuell fastsetting av fordel ved privat bruk er oppfylt, jf. Finansdepartementets forskrift til skatteloven § 5-13-1 fjerde ledd fjerde punktum, fastsettes fordelen til kr 3,40 per kilometer.

**Praktiske konsekvenser:**

Disse nye satsene vil påvirke:
- Beregning av forskuddstrekk for 2026
- Skattefastsetting for inntektsåret 2026
- Utbetaling av skattefrie godtgjørelser
- Verdsettelse av naturalytelser

**Viktige endringer fra 2025:**

Satsene er justert for å reflektere prisutviklingen og endringer i kostnader knyttet til:
- Reiseutgifter og opphold
- Bilhold og drivstoffpriser
- Generell prisutvikling

**Råd til bedrifter:**

- Oppdater lønns- og regnskapssystemer med nye satser
- Informer ansatte om endringene
- Gjennomgå eksisterende avtaler om godtgjørelser
- Vurder om firmabilordninger må justeres

Disse satsene er viktige for korrekt beregning av skatt og godtgjørelser i 2026.`,
      date: '7. november 2025',
      category: 'Skatt',
      image: 'https://readdy.ai/api/search-image?query=Norwegian%20tax%20rates%20and%20salary%20calculations%20for%202026%2C%20official%20tax%20documents%20with%20calculator%20and%20charts%2C%20professional%20accounting%20desk%20with%20papers%20showing%20advance%20tax%20rates%20and%20car%20allowances&width=400&height=250&seq=news-forskuddssatser-2026&orientation=landscape',
      source: 'Sticos'
    },
    {
      id: 1,
      title: 'Betaling av forskuddstrekk - Viktige endringer fra 2026',
      excerpt: 'Fra 1. januar 2026 skal du betale skatten du har trukket fra de ansattes lønn direkte til Skatteetaten. Fristen er første virkedag etter lønnsutbetalingen.',
      fullContent: `Fra 1. januar 2026 innføres det viktige endringer i hvordan forskuddstrekk skal betales til Skatteetaten.

**Hovedendringer:**

**Direkte betaling til Skatteetaten**

Fra 1. januar 2026 skal du betale skatten du har trukket fra de ansattes lønn direkte til Skatteetaten. Fristen for å betale skattetrekket er første virkedag etter lønnsutbetalingen. Dette betyr at du ikke lenger skal overføre skattetrekket til en sperret bankkonto.

**Overgangsmåned januar 2026**

Januar 2026 vil være en overgangsmåned fra terminvis til fortløpende innbetaling.

Dette betyr at:
- Betaling av 6. termin 2025 skal skje fra skattetrekkskonto, senest 15. januar 2026
- Du må stoppe overføringen til skattetrekkskontoen din etter 6. termin 2025
- Fra og med 1. januar 2026 skal forskuddstrekk på lønn betales direkte til Skatteetaten senest første virkedag etter lønnsutbetaling

**Betalingsinformasjon:**

Forskuddstrekket skal betales til Skatteetatens konto: **7694 05 24802**

**Hva skjer med skattetrekkskontoen?**

Bankene kan ha forskjellige løsninger for hva som skjer med selve kontoen:
- Noen banker kan gjøre om kontoen til ordinær konto
- Andre banker kan stenge kontoen
- Følg med på informasjon fra din bank

**Penger igjen på skattetrekkskontoen?**

Hvis det er igjen penger på skattetrekkskontoen etter betaling av 6. termin 2025:

**Beløp under 12 000 kroner:**
- Frigis automatisk etter 10. april 2026
- Du trenger ikke å foreta deg noe

**Beløp over 12 000 kroner:**
- Du må søke på samme måte som i dag
- Du kan søke Skatteetaten om dette allerede fra 16. januar 2026

**A-meldingen rapporteres som før**

- Frist for rapportering av a-meldingen er fremdeles den 5. i den påfølgende måneden
- Husk å oppgi lønnsutbetalingsdato i a-meldingen

**Praktiske konsekvenser for bedrifter:**

**Økt administrativt arbeid:**
- Må følge opp betalinger oftere (etter hver lønnskjøring)
- Tettere oppfølging av frister

**Bedre likviditetsstyring:**
- Ikke lenger binding av midler på sperret konto
- Mer fleksibel kontantstrøm

**Viktige datoer å huske:**
- 15. januar 2026: Siste betaling fra skattetrekkskonto (6. termin 2025)
- 1. januar 2026: Start på ny ordning med direkte betaling
- 16. januar 2026: Kan søke om frigjøring av midler over 12 000 kr
- 10. april 2026: Automatisk frigjøring av beløp under 12 000 kr

**Forberedelser du bør gjøre nå:**

1. **Kontakt din bank** for å avklare hva som skjer med skattetrekkskontoen
2. **Oppdater rutiner** for lønnskjøring og skatteinnbetaling
3. **Informer ansvarlige** om de nye fristene og prosedyrene
4. **Vurder systemoppdateringer** for å håndtere den nye betalingsrutinen

**Råd fra ekspertene:**

- Start planleggingen tidlig for å unngå problemer i overgangen
- Sørg for at alle som håndterer lønn er informert om endringene
- Test de nye rutinene før januar 2026
- Ha en backup-plan for betalinger hvis noe går galt

Denne endringen er en del av Skatteetatens modernisering og digitalisering av skatteinnkrevingen. Målet er å forenkle prosessene og redusere administrasjonen på sikt.`,
      date: '1. oktober 2025',
      category: 'Skatt',
      image: 'https://readdy.ai/api/search-image?query=Norwegian%20tax%20office%20payment%20system%2C%20direct%20payment%20to%20tax%20authorities%2C%20payroll%20tax%20administration%2C%20modern%20digital%20tax%20payment%20process%20with%20calculator%20and%20official%20documents&width=400&height=250&seq=news-forskuddstrekk-2026&orientation=landscape',
      source: 'Skatteetaten'
    },
    {
      id: 2,
      title: 'Statsbudsjettet 2026 - Del 2',
      excerpt: 'Regjeringen styrker Arbeidstilsynet og Fair Play Bygg, reduserer engangsstønaden og gjør endringer i trygdeavgift og merverdiavgift. Nye regler for uføretrygd og elektriske kjøretøy.',
      fullContent: `Regjeringen har lagt frem del 2 av statsbudsjettet for 2026 med viktige endringer innen arbeidsrett, trygderett og merverdiavgift.

**Arbeidsrett:**

**Regjeringen styrker Arbeidstilsynet og Fair Play Bygg**

Det norske arbeidslivet blir utfordret av useriøse aktører, sosial dumping og lav organisasjonsgrad i utsatte bransjer. Derfor styrker regjeringen innsatsen til Arbeidstilsynet og Fair Play Bygg med 10 og 2 millioner kroner i statsbudsjettet for neste år.

**Trygderett:**

**Engangsstønad:**

Regjeringen foreslår å redusere engangsstønaden fra 92 648 kr til 60 000 kr.

Engangsstønaden gir familien en engangsutbetaling som kan dekke utgifter i sammenheng med å få barn, men er i dag betydelig høyere enn det som er estimert å være kostnaden ved å ha barn det første året.

**Trygdeavgift:**

Regjeringen foreslår å redusere trygdeavgiftssatsene på lønn/trygd og næringsinntekt med 0,1 prosentenhet i 2026, til henholdsvis 7,6 og 10,8 prosent. Satsen for pensjon videreføres på 5,1 prosent. Nedre grense for å betale trygdeavgift foreslås videreført uendret på 99 650 kroner.

**Uføretrygd:**

Regjeringen foreslår to endringer som gjør det mer lønnsomt å jobbe ved siden av uføretrygd. Noen uføre får i dag en forholdsvis stor reduksjon i uføretrygden hvis de arbeider. Regjeringen foreslår å sette et tak på hvor høy reduksjonen kan være, slik at de får lavere reduksjon enn i dag. Regjeringen foreslår også en endring som gjør at særlig gifte og samboende med minsteytelse får mer igjen for å arbeide.

**Merverdiavgift:**

**Endring i fritaket for elektriske kjøretøy**

Beløpsgrensen i fritaket for omsetning og leasing av elektriske kjøretøy foreslås redusert fra 500 000 kroner til 300 000 kroner fra 1. januar 2026. Videre varsler regjeringen at fritaket vil bli foreslått avviklet fra 1. januar 2027.

**Tapsføring av krav på nærstående**

Regjeringen foreslår at retten til å tapsføre merverdiavgift på kundefordringer skal falle bort etter 24 måneder, dersom merverdiavgiften knytter seg til en fordring på et nærstående selskap. Etter forslaget starter tidsperioden å løpe fra når den aktuelle merverdiavgiften ble beregnet, og ikke når fordringen ble etablert. Endringene foreslås å tre i kraft med virkning fra 1. januar 2026.

**Merverdiavgift ved internasjonal handel med tjenester**

Avgiftsplikten for fjernleverbare tjenester som er til bruk i merverdiavgiftsområdet (Norge) foreslås utvidet:

- Utvidet plikt til å beregne merverdiavgift på fjernleverbare tjenester til bruk i Norge for multinasjonale foretak som er hjemmehørende både i utlandet og i Norge
- Utvidet rett til fradrag og refusjon for multinasjonale foretak som er hjemmehørende både i utlandet og i Norge

Endringene foreslås å tre i kraft fra 1. juli 2026.

**Konsekvenser for bedrifter og privatpersoner:**

**For privatpersoner:**
- Lavere engangsstønad ved fødsel
- Redusert trygdeavgift gir mer igjen av lønn
- Bedre vilkår for uføre som ønsker å arbeide

**For bedrifter:**
- Strengere kontroll fra Arbeidstilsynet
- Endringer i MVA-regler for elektriske kjøretøy
- Nye regler for tapsføring av kundefordringer
- Endringer i MVA ved internasjonal handel

**Praktiske råd:**

- Følg med på implementeringen av de nye reglene
- Vurder konsekvensene for din bedrift eller situasjon
- Kontakt din regnskapsfører for personlig rådgivning
- Planlegg for endringene som trer i kraft i 2026 og 2027

Vi følger utviklingen i budsjettbehandlingen og vil komme med flere oppdateringer etter hvert som mer informasjon blir tilgjengelig.`,
      date: '15. oktober 2025',
      category: 'Skatt',
      image: 'https://readdy.ai/api/search-image?query=Norwegian%20government%20budget%20documents%20part%202%2C%20official%20state%20budget%20proposal%202026%20with%20focus%20on%20labor%20rights%20and%20social%20benefits%2C%20professional%20government%20office%20environment%20with%20Norwegian%20flag%20and%20official%20papers&width=400&height=250&seq=news-statsbudsjett-2026-del2&orientation=landscape',
      source: 'Sticos'
    },
    {
      id: 3,
      title: 'Statsbudsjettet 2026 - Del 1',
      excerpt: 'Onsdag 15. oktober la regjeringen frem sitt forslag til statsbudsjett for neste år. Hovedpunktene inkluderer endringer i formuesskatt, trinnskatt og ulike fradrag. Nye regler for verdipapirfond og tiltak for å tette skattehull.',
      fullContent: `Onsdag 15. oktober la regjeringen frem sitt forslag til statsbudsjett for neste år. Her er hovedpunktene:

**Skatt:**

Regjeringen vil holde de samlede skattene og avgiftene på norske privatpersoner og bedrifter uendret. I forslaget til 2026-budsjett holdes skatter og avgifter på norske privatpersoner og bedrifter på samme nivå som i dag.

**Formuesskatt**

Formuesskatt til kommuner justeres fra 0,525 prosent til 0,35 prosent.

Satsen for formuesskatt til staten for formuer over bunnfradraget og inntil 21,5 millioner kroner justeres fra 0,475 prosent til 0,65 prosent.

Satsen på formuesskatt til staten for formuer over 21,5 millioner kroner justeres fra 0,575 prosent til 0,75 prosent.

Innslagspunktet for trinn 2 i formuesskatten opprettholdes reelt, noe som innebærer en økning fra 20,7 millioner kroner til 21,5 millioner kroner.

Bunnfradraget i formuesskatten økes fra 1,76 millioner kroner til 1,9 millioner kroner.

Det foreslås å ta i bruk en revidert og mer treffsikker modell for verdsetting av bolig for formuesskatteformål fra 2026.

Regjeringen foreslår en ny og varig ordning for utsettelse av betaling av formuesskatt på virksomhetsformue.

**Inntektskatt**

Regjeringen foreslår at satsene i trinnskatten holdes uendret i 2026. Innslagspunktene i trinnskatten foreslås justert med anslått lønnsvekst på 4,0 prosent.

**Trinnskatt 2026:**
- Trinn 1: 1,7 % - 226 100 - 318 300
- Trinn 2: 4,0 % - 318 300 - 725 050
- Trinn 3: 13,7 % - 725 050 - 980 100
- Trinn 4: 16,7 % - 980 100 - 1 467 200
- Trinn 5: 17,7 % - 1 467 200

**Fradrag og justeringer:**

Personfradraget økes fra 108 550 kroner til 114 210 kroner.

Finnmarksfradraget økes fra 30 000 kroner til 45 000 kroner.

Øvre grense i minstefradraget i lønns og trygdeinntekt økes til 95 700 kroner og øvre grense i minstefradraget i pensjonsinntekt økes til 75 400 kroner.

Fagforeningsfradraget økes til 8 700 kroner.

Det foreslås å prisjustere kilometersatsen og bunnbeløpet i reisefradraget. Kilometersatsen økes fra 1,83 til 1,87 kroner, og bunnbeløpet fra 15 250 til 15 600 kroner.

Foreldrefradraget foreslås redusert til 15 000 kroner for ett barn og 10 000 kroner for hvert barn utover det første.

Grensen for maksimalt fradrag for innbetaling til IPS foreslås økt fra 15 000 kroner til 25 000 kroner.

Særskilt fradrag i barnepensjon fra folketrygden og andre ordninger prisjusteres og økes til 32 600 kroner i året.

**Skattebegrensning:**

Det foreslås å øke den skattefrie nettoinntekten med 10 000 kroner. Endringen skal gjelde fra 2025, for at flere skal få del i skattelettelsen. I 2026 prisjusteres grensene for skattefri nettoinntekt.

Det foreslås å redusere grensen for inntektsføring av innestående på jordbrukskonto fra 85 til 80 prosent fra 1. januar 2026.

**Utsettelsesordning for formuesskatt**

Departementet foreslår en ordning der skattyter kan kreve utsettelse av betaling av formuesskatt i inntil tre år dersom formuesskatten overstiger 30 000 kroner. Forslaget retter seg mot eiere av virksomhetsformue. Dette omfatter formue i børsnoterte og ikke-børsnoterte aksjer, andeler i selskaper med deltakerfastsetting, aksjesparekonto og driftsmidler og næringseiendom i enkeltpersonforetak. For at ordningen skal treffe skattytere med reelle likviditetsproblemer, foreslår departementet at utsatt formuesskatt ilegges en rente på markedsvilkår.

**Verdipapirfond og fondskonto**

Det foreslås endringer i skattereglene for verdipapirfond og fondskonto for å unngå dobbeltbeskatning og gi norske fond bedre vilkår. Dette skal gjøre det unødvendig å flytte fond til utlandet for å spare skatt. Forslagene innebærer blant annet at fond fritas for ordinær beskatning av aksje- og renteinntekter, men får en sjablongbeskatning på 1 % av mottatt utbytte. Øvrig beskatning skal skje hos andelseierne, uten endringer i hvordan de skattlegges. Reglene skal gjelde for UCITS-fond, nasjonale fond og tilsvarende EØS-fond. Endringene skal tre i kraft fra inntektsåret 2026.

**Tetting av skattehull**

Regjeringen foreslår å tette flere skattehull. Blandt tiltakene er å stoppe den såkalte «flertrinnsmodellen», som har gjort det mulig å unngå gevinstbeskatning ved salg av boliger og tomter. Modellen brukes både for å unngå gevinstbeskatning ved salg av eldre og etablert boligmasse og ved boligutvikling. Noe av grunnlaget for modellen er at Skatteetaten har avgitt bindende forhåndsuttalelser hvor den konkluderer med at modellen ikke rammes av den skatterettslige omgåelsesregelen. Regjeringen ønsker derfor å avskjære muligheten for skattefri fusjon mellom aksjeselskap og borettslag.

Regjeringen foreslår også å endre en regel som gjør at finansforetak med virksomhet i utlandet kan få doble rentefradrag.

**Konsekvenser for bedrifter og privatpersoner:**

Endringene i formuesskatten vil påvirke mange bedriftseiere og privatpersoner med høy formue. Den nye modellen for verdsetting av bolig kan få betydning for mange huseiere.

Økningen i personfradraget og andre fradrag vil gi skattelette for de fleste lønnstakere og pensjonister.

**Praktiske råd:**

- Vurder konsekvensene av endringene i formuesskatten for din situasjon
- Planlegg for eventuelle endringer i skatteberegningen
- Følg med på ytterligere detaljer når budsjettet behandles i Stortinget
- Kontakt din regnskapsfører for personlig rådgivning

Vi føler utviklingen i budsjettbehandlingen og vil komme med flere oppdateringer etter hvert som mer informasjon blir tilgjengelig.`,
      date: '15. oktober 2025',
      category: 'Skatt',
      image: 'https://readdy.ai/api/search-image?query=Norwegian%20government%20budget%20documents%20and%20tax%20papers%20on%20professional%20desk%2C%20official%20state%20budget%20proposal%202026%2C%20financial%20planning%20documents%2C%20calculator%20and%20charts%2C%20clean%20business%20environment%20with%20Norwegian%20flag%20elements&width=400&height=250&seq=news-statsbudsjett-2026&orientation=landscape',
      source: 'Sticos.no'
    },
    {
      id: 4,
      title: 'Skjult trussel mot regnskapsførere: Her er sikkerhetsmåneden!',
      excerpt: 'Høsten er tid for jakt, men svindlere jakter hele året på deg og meg. Regnskap Norge hiver seg på den nasjonale sikkerhetsmåneden som er oktober.',
      fullContent: `Regnskapsførere er spesielt utsatt for cyberkriminalitet og svindel. I forbindelse with den nasjonale sikkerhetsmåneden i oktober setter Regnskap Norge fokus på de skjulte truslene som rammer bransjen.

**Hvorfor er regnskapsførere særlig utsatt?**

Regnskapsførere håndterer sensitive finansielle data og har tilgang til bedrifters mest konfidensielle informasjon. Dette gjør dem til attraktive mål for kriminelle.

**Vanlige trusler mot regnskapsførere:**

**1. Phishing-angrep**
- Falske e-poster som utgir seg for å være fra Skatteetaten, banker eller klienter
- Forsøk på å stjele påloggingsinformasjon til regnskapssystemer
- Falske fakturaer og betalingsanmodninger

**2. Ransomware**
- Skadevare som krypterer filer og krever løsepenger
- Kan lamme hele regnskapsvirksomheten
- Spesielt farlig under travle perioder som årsoppgjør

**3. Sosial manipulasjon**
- Telefonsvindel hvor kriminelle utgir seg for å være klienter
- Forsøk på å få tilgang til sensitiv informasjon
- Falske henvendelser om "hastebetalinger"

**4. Usikre hjemmekontor**
- Svake WiFi-nettverk hjemme
- Manglende sikkerhetsprogramvare på private enheter
- Blanding av private og profesjonelle aktiviteter

**Sikkerhetstiltak for regnskapsførere:**

**Tekniske tiltak:**
- Bruk tofaktor-autentisering på alle systemer
- Hold programvare og sikkerhetsprogrammer oppdatert
- Lag regelmessige sikkerhetskopier
- Bruk VPN ved arbeid hjemmefra
- Installer brannmur og antivirus på alle enheter

**Organisatoriske tiltak:**
- Etabler klare rutiner for håndtering av sensitive data
- Gjennomfør regelmessig opplæring av ansatte
- Lag beredskapsplaner for sikkerhetsbrudd
- Verifiser alltid betalingsanmodninger via telefon
- Begrens tilgang til systemer basert på behov

**Personlige sikkerhetstips:**
- Vær skeptisk til uventede e-poster og telefonhenvendelser
- Aldri oppgi påloggingsinformasjon via e-post eller telefon
- Sjekk avsenderadresser nøye før du svarer på e-poster
- Rapporter mistenkelige henvendelser til politiet

**Hva gjør du hvis du blir utsatt?**

1. **Koble fra internett** umiddelbart hvis du mistenker angrep
2. **Kontakt IT-support** eller sikkerhetsleverandør
3. **Anmeld forholdet** til politiet
4. **Informer berørte klienter** så snart som mulig
5. **Dokumenter** alt som har skjedd

**Ressurser og hjelp:**

- Nasjonal sikkerhetsmyndighet (NSM): nsm.no
- Politiets nettpatrulje: politiet.no
- Regnskap Norge sine sikkerhetstips: regnskapnorge.no

**Sikkerhetsmåneden oktober:**

Gjennom hele oktober arrangerer Regnskap Norge webinarer og kurs om cybersikkerhet. Meld deg på våre arrangementer for å lære mer om hvordan du kan beskytte deg og din virksomhet.

Husk: Sikkerhet er ikke en engangsinvestering, men en kontinuerlig prosess. Hold deg oppdatert og vær alltid på vakt!`,
      date: '25. september 2024',
      category: 'Teknologi',
      image: 'https://regnskapnorge.no/contentassets/c4cc12fc70a246dfad4082364fd3c205/adobestock_413817942.jpeg',
      source: 'regnskapnorge.no'
    },
    {
      id: 5,
      title: 'Regnskap Norge med på støtteskriv om at tastefeil ikke bør føre til tilleggsskatt',
      excerpt: 'I dag praktiserer Skatteetaten unntaket i skatteforvaltningsloven for åpenbare regne- og skrivefeil svært snevert. Tolkningen av unntaket er sentralt i en sak som nå er anket til Høyesterett.',
      fullContent: `Regnskap Norge har sluttet seg til et støtteskriv som argumenterer for at enkle tastefeil ikke bør føre til tilleggsskatt. Saken har nå kommet til Høyesterett og kan få stor betydning for hvordan Skatteetaten håndterer slike feil fremover.

**Bakgrunn for saken:**

En skatteyter gjorde en enkel tastefeil i selvangivelsen som førte til at Skatteetaten ila tilleggsskatt. Feilen var åpenbar og ble oppdaget relativt raskt, men Skatteetaten har likevel fastholdt kravet om tilleggsskatt.

**Hva sier loven?**

Skatteforvaltningsloven § 13-1 inneholder et unntak for "åpenbare regne- og skrivefeil". Dette unntaket skal sikre at enkle feil ikke får uforholdsmessig store konsekvenser.

**Skatteetatens praksis:**

I dag praktiserer Skatteetatet dette unntaket svært snevert. De krever ofte at:
- Feilen må være helt åpenbar for alle
- Det må ikke være rom for tvil om hva som var ment
- Feilen må oppdages og meldes inn meget raskt

**Regnskap Norges standpunkt:**

Regnskap Norge mener at Skatteetatens praksis er for streng og at:

**1. Enkle tastefeil bør omfattes av unntaket**
- Når det er klart at det dreier seg om en ren tastefeil
- Når feilen ikke påvirker den materielle forståelsen av forholdet
- Når skatteyteren handler i god tro

**2. Proporsjonalitetsprinsippet må vektlegges**
- Sanksjonen må stå i forhold til overtredelsen
- Enkle feil bør ikke føre til samme konsekvenser som bevisst feilinformasjon
- Hensynet til rettferdighet må veie tungt

**3. Forutsigbarhet for næringen**
- Regnskapsførere og skatteyterne må kunne stole på at enkle feil ikke ikke føre til dramatiske konsekvenser
- Praktis må være konsistent og forutsigbar

**Konsekvenser av saken:**

Høyesteretts avgjørelse kan få stor betydning for:

**Regnskapsførere:**
- Mindre risiko ved håndtering av klienters skatteforhold
- Klarere retningslinjer for når feil får konsekvenser
- Bedre forutsigbarhet i arbeidet

**Skatteyterne:**
- Mer rimelig behandling av enkle feil
- Redusert risiko for uforholdsmessige sanksjoner
- Økt tillit til skattesystemet

**Skatteetaten:**
- Må eventuelt endre sin praksis
- Klarere retningslinjer for når tilleggsskatt skal ilegges
- Mer nyansert tilnærming til feilbehandling

**Praktiske råd:**

Inntil saken er avgjort anbefaler vi:

1. **Ekstra nøye kontroll** av alle tall og opplysninger
2. **Rask oppfølging** hvis du oppdager feil
3. **Dokumentasjon** av alle endringer og korrigeringer
4. **Kontakt Skatteetaten** umiddelbart ved oppdagelse av feil

**Fremtidige perspektiver:**

Uavhengig av utfallet i Høyesterett jobber Regnskap Norge for:
- Mer rimelig praksis fra Skatteetaten
- Klarere retningslinjer for bransjen
- Bedre dialog mellom myndigheter og næring

Saken viser viktigheten av at bransjeorganisasjoner engasjerer seg i principesaker som påvirker medlemmenes arbeidsvilkår.

Vi følger saken tett og vil informere medlemmene så snart Høyesterett har avsagt dom.`,
      date: '20. september 2024',
      category: 'Skatt',
      image: 'https://regnskapnorge.no/contentassets/2367695872cd432f9e2597e3ce1f1c5d/adobestock_1561040890.jpeg',
      source: 'regnskapnorge.no'
    },
    {
      id: 6,
      title: 'Merverdiavgiftsreglene i EU knyttet til vare- og tjenesteleveranse',
      excerpt: 'Nye kurs om MVA-regler i EU som påvirker norske bedrifter med grenseoverskridende handel. Viktig oppdatering for regnskapsførere.',
      fullContent: `Regnskap Norge tilbyr nå et omfattende nettkurs om merverdiavgiftsreglene i EU som påvirker norske bedrifter med grenseoverskridende handel.

**Kursets innhold:**

Kurset dekker de viktigste aspektene ved EU's MVA-regelverk som norske bedrifter må forholde seg til:

**1. Grunnleggende MVA-prinsipper i EU**
- Forskjeller mellom norske og EU-regler
- Leveringssted for varer og tjenester
- Registreringsplikt i andre EU-land

**2. Vare- og tjenesteleveranser**
- Regler for fysiske vareleveranser
- Digitale tjenester og e-handel
- Spesielle regler for enkelte bransjer

**3. Praktiske utfordringer**
- Dokumentasjonskrav
- Rapportering og innlevering
- Håndtering av refusjoner

**4. Brexit-konsekvenser**
- Endrede regler for handel med Storbritannia
- Nye dokumentasjonskrav
- Overgangsordninger

**Målgruppe:**

Kurset er spesielt relevant for:
- Regnskapsførere med klienter som handler med EU
- Bedrifter som selger varer eller tjenester til EU-land
- Selskaper som vurderer etablering i EU

**Praktiske eksempler:**

Kurset inneholder mange praktiske eksempler fra virkeligheten:
- Norsk nettbutikk som selger til Tyskland
- Konsulentselskap som leverer tjenester i Frankrike
- Produksjonsbedrift med lager i Nederland

**Autoriserings:**

Kurset gir 2,5 timer autorisering innen skatt og er godkjent av Finanstilsynet.

**Påmelding:**

Kurset gjennomføres som nettkurs og kan tas når det passer deg. Medlemmer av Regnskap Norge får rabatt på kursprisen.

**Hvorfor er dette viktig nå?**

EU har de siste årene innført flere endringer i MVA-regelverket som påvirker norske bedrifter:
- Nye regler for e-handel fra 2021
- Endrede terskelverdier for registrering
- Strengere dokumentasjonskrav

**Konsekvenser ved feil:**

Feil håndtering av EU's MVA-regler kan føre til:
- Bøter og sanksjoner fra EU-myndigheter
- Dobbeltbeskatning
- Problemer med refusjoner
- Konkurranseulemper

**Oppfølging:**

Etter kurset får deltakerne tilgang til:
- Oppdaterte maler og sjekklister
- Kontaktinformasjon til eksperter
- Nyhetsbrev med regelverksendringer

Dette kurset er en investering i kompetanse som kan spare bedrifter for store kostnader og problemer senere.`,
      date: '15. januar 2025',
      category: 'MVA',
      image: 'https://readdy.ai/api/search-image?query=European%20Union%20flag%20with%20VAT%20tax%20documents%20and%20Norwegian%20business%20papers%2C%20cross-border%20trade%20regulations%2C%20professional%20accounting%20environment%20with%20EU%20and%20Norway%20symbols&width=400&height=250&seq=news-eu-mva-regler-2025&orientation=landscape',
      source: 'regnskapnorge.no'
    },
    {
      id: 7,
      title: 'Naturalytelser og velferdstiltak: Del 2 - Nye regler for 2025',
      excerpt: 'Oppdatert kurs om naturalytelser og velferdstiltak med de nyeste endringene som trer i kraft i 2025. Viktig for alle som jobber med lønn.',
      fullContent: `Regnskap Norge presenterer del 2 av kurset om naturalytelser og velferdstiltak, med fokus på de viktigste endringene som trer i kraft i 2025.

**Hva er nytt i 2025?**

Skatteetaten har innført flere viktige endringer i regelverket for naturalytelser og velferdstiltak:

**1. Nye satser og beløpsgrenser**
- Oppdaterte satser for fri bil og fri telefon
- Endrede beløpsgrenser for skattefrie velferdstiltak
- Nye regler for hjemmekontor-godtgjørelse

**2. Utvidede muligheter for velferdstiltak**
- Økt ramme for skattefrie gaver til ansatte
- Nye regler for helse- og velferdstiltak
- Klargjøring av regler for teambuilding og sosiale arrangementer

**3. Strengere dokumentasjonskrav**
- Nye krav til dokumentasjon av naturalytelser
- Endrede rapporteringsrutiner
- Økte krav til verdsettelse

**Kursets hovedtemaer:**

**Naturalytelser:**
- Fri bil - beregning og rapportering
- Fri bolig og losji
- Fri kost og andre naturalytelser
- Verdsettelse og dokumentasjon

**Velferdstiltak:**
- Helse- og velferdstiltak for ansatte
- Idrettsaktiviteter og treningssentre
- Sosiale arrangementer og teambuilding
- Gaver og oppmerksomheter

**Praktiske utfordringer:**
- Grensedragning mellom skattepliktige og skattefrie ytelser
- Håndtering av blandede formål
- Dokumentasjon og bevisbyrde
- Rapportering i a-melding

**Vanlige feil og fallgruver:**

Kurset går gjennom de vanligste feilene som gjøres:
- Feil verdsettelse av naturalytelser
- Manglende dokumentasjon
- Feil kategorisering av velferdstiltak
- Problemer med rapportering

**Praktiske eksempler:**

- Bedrift som tilbyr fri bil til ansatte
- Selskap med treningsstudio på arbeidsplassen
- Firma som arrangerer julebord og sommerfest
- Bedrift med hjemmekontor-ordning

**Autoriserings:**

Kurset gir 2,5 timer autorisering innen skatt og er godkjent av Finanstilsynet.

**Målgruppe:**

Kurset er relevant for:
- Regnskapsførere som håndterer lønn
- HR-ansvarlige og lønnsansvarlige
- Bedriftsledere som planlegger personalytelser
- Revisorer som kontrollerer lønnsrapportering

**Konsekvenser ved feil:**

Feil håndtering av naturalytelser og velferdstiltak kan føre til:
- Tilleggsskatt og renter
- Bøter fra Skatteetaten
- Problemer ved skatterevisjoner
- Urettferdig behandling av ansatte

**Oppfølging:**

Deltakerne får tilgang til:
- Oppdaterte beregningsverktøy
- Sjekklister for dokumentasjon
- Kontakt med fageksperter
- Løpende oppdateringer om regelverksendringer

Dette kurset er essensielt for alle som jobber med lønn og personaladministrasjon i 2025.`,
      date: '10. januar 2025',
      category: 'Lønn',
      image: 'https://readdy.ai/api/search-image?query=Norwegian%20employee%20benefits%20and%20welfare%20programs%20documentation%2C%20salary%20administration%20papers%20with%20calculator%2C%20professional%20HR%20and%20payroll%20management%20environment&width=400&height=250&seq=news-naturalytelser-2025&orientation=landscape',
      source: 'regnskapnorge.no'
    }
  ];

  const categories = ['Alle', 'Skatt', 'Regnskapsregler', 'Teknologi', 'MVA', 'Lønn', 'Bærekraft', 'Årsregnskap'];
  const [selectedCategory, setSelectedCategory] = useState('Alle');

  const filteredNews = selectedCategory === 'Alle' 
    ? latestNews 
    : latestNews.filter(news => news.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl mb-6">
            Nyhetsbrev
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Hold deg oppdatert på de siste nyhetene innen regnskap, skatt og økonomi. 
            Få ekspertinnsikt og praktiske tips direkte i innboksen din.
          </p>
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Abonner på vårt nyhetsbrev
              </h2>
              <p className="text-gray-600">
                Få de viktigste nyhetene og oppdateringene innen regnskap direkte i e-posten din
              </p>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg text-center">
                Takk for at du abonnerer! Du vil snart motta vårt nyhetsbrev.
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-center">
                Noe gikk galt. Vennligst prøv igjen.
              </div>
            )}

            <form onSubmit={handleSubmit} data-readdy-form id="nyhetsbrev_abonnement">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="navn" className="block text-sm font-medium text-gray-700 mb-2">
                    Fullt navn *
                  </label>
                  <input
                    type="text"
                    id="navn"
                    name="navn"
                    value={formData.navn}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Ditt fulle navn"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-post *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="din@epost.no"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="bedrift" className="block text-sm font-medium text-gray-700 mb-2">
                    Bedriftsnavn
                  </label>
                  <input
                    type="text"
                    id="bedrift"
                    name="bedrift"
                    value={formData.bedrift}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Din bedrift AS"
                  />
                </div>
                <div>
                  <label htmlFor="interesser" className="block text-sm font-medium text-gray-700 mb-2">
                    Interesseområder
                  </label>
                  <select
                    id="interesser"
                    name="interesser"
                    value={formData.interesser}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="">Velg interesseområde</option>
                    <option value="regnskap">Regnskap og bokføring</option>
                    <option value="skatt">Skatt og MVA</option>
                    <option value="lonn">Lønn og personaladministrasjon</option>
                    <option value="teknologi">Teknologi og digitalisering</option>
                    <option value="barekraft">Bærekraft og ESG</option>
                    <option value="alle">Alle områder</option>
                  </select>
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full" 
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Abonnerer...
                  </>
                ) : (
                  <>
                    <i className="ri-mail-line mr-2"></i>
                    Abonner på nyhetsbrev
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-4">
              Siste nyheter
            </h2>
            <p className="text-lg text-gray-600">
              Oppdateringer fra regnskapsverdenen og våre eksperter
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* News Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredNews.map((news) => (
              <article key={news.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-300">
                <img
                  src={news.image}
                  alt={news.title}
                  className="w-full h-48 object-cover object-top"
                />
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {news.category}
                    </span>
                    <span className="text-xs text-gray-500">{news.date}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2">
                    {news.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {news.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      Kilde: {news.source}
                    </span>
                    <button 
                      onClick={() => handleReadMore(news)}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium cursor-pointer"
                    >
                      Les mer <i className="ri-arrow-right-line ml-1"></i>
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Article Modal */}
      {showModal && selectedArticle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {selectedArticle.category}
                </span>
                <span className="text-sm text-gray-500">{selectedArticle.date}</span>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            <div className="p-6">
              <img
                src={selectedArticle.image}
                alt={selectedArticle.title}
                className="w-full h-64 object-cover object-top rounded-lg mb-6"
              />
              
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                {selectedArticle.title}
              </h1>
              
              <div className="prose prose-lg max-w-none">
                {selectedArticle.fullContent.split('\n\n').map((paragraph, index) => {
                  if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                    return (
                      <h3 key={index} className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                        {paragraph.replace(/\*\*/g, '')}
                      </h3>
                    );
                  } else if (paragraph.startsWith('**') && paragraph.includes(':**')) {
                    return (
                      <h4 key={index} className="text-lg font-semibold text-gray-900 mt-4 mb-2">
                        {paragraph.replace(/\*\*/g, '')}
                      </h4>
                    );
                  } else if (paragraph.startsWith('- ')) {
                    const listItems = paragraph.split('\n').filter(item => item.startsWith('- '));
                    return (
                      <ul key={index} className="list-disc list-inside space-y-1 mb-4">
                        {listItems.map((item, itemIndex) => (
                          <li key={itemIndex} className="text-gray-700">
                            {item.substring(2)}
                          </li>
                        ))}
                      </ul>
                    );
                  } else if (paragraph.startsWith('1. ')) {
                    const listItems = paragraph.split('\n').filter(item => item.startsWith('1. '));
                    return (
                      <ol key={index} className="list-decimal list-inside space-y-1 mb-4">
                        {listItems.map((item, itemIndex) => (
                          <li key={itemIndex} className="text-gray-700">
                            {item.substring(3)}
                          </li>
                        ))}
                      </ol>
                    );
                  } else {
                    return (
                      <p key={index} className="text-gray-700 mb-4 leading-relaxed">
                        {paragraph}
                      </p>
                    );
                  }
                })}
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-4">
                  Kilde: {selectedArticle.source}
                </p>
                <div className="flex items-center justify-between">
                  <Button onClick={closeModal} variant="outline">
                    Lukk artikkel
                  </Button>
                  <div className="flex items-center space-x-4">
                    <button className="text-gray-400 hover:text-blue-600 cursor-pointer">
                      <i className="ri-share-line text-xl"></i>
                    </button>
                    <button className="text-gray-400 hover:text-blue-600 cursor-pointer">
                      <i className="ri-bookmark-line text-xl"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Newsletter Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-4">
              Hvorfor abonnere på vårt nyhetsbrev?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Spar tid
              </h3>
              <p className="text-gray-600">
                Få de viktigste nyhetene samlet på ett sted, slik at du ikke trenger å søke rundt på nettet.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lightbulb-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Ekspertinnsikt
              </h3>
              <p className="text-gray-600">
                Få praktiske tips og råd fra våre erfarne regnskapsførere og økonomirådgivere.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-notification-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Viktige frister
              </h3>
              <p className="text-gray-600">
                Bli varslet om viktige frister og endringer som påvirker din bedrift.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
